// --- EFECTO PARALLAX EN HERO ---
// Mueve el fondo del hero a 0.4x la velocidad de scroll para crear profundidad.
// Desactivado en móvil (< 768px) y en modo editor.
import { store } from '../core/store.js';
import { registerPostRender } from '../core/render.js';

let ticking = false;
let activeHeroes = [];
let activeGhosts = [];

/**
 * Recoge todos los heroes con imagen de fondo y los registra para parallax.
 */
function collectHeroes() {
    // Heroes con background-image (variantes v1/v2)
    activeHeroes = Array.from(
        document.querySelectorAll('.unsplash-bg-target, .hero-section')
    ).filter(el => {
        // `data-unsplash-bg` = su foto está al llegar (la pone unsplash.js): así el encuadre ya
        // está puesto cuando aparece y no salta.
        if (el.hasAttribute('data-unsplash-bg')) return true;
        const style = el.style.backgroundImage || window.getComputedStyle(el).backgroundImage;
        return style && style !== 'none';
    });

    // Elementos fantasma (Ghost Text)
    activeGhosts = Array.from(document.querySelectorAll('.ghost-text'));
}

/**
 * Aplica el desplazamiento parallax en el RAF.
 */
function applyParallax() {
    const scrollY = window.scrollY;

    activeHeroes.forEach(el => {
        const rect = el.getBoundingClientRect();
        // Solo procesar si está visible en pantalla
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;

        // Offset basado en posición del elemento relativa al centro del viewport
        const distanceFromCenter = (window.innerHeight * 0.5) - rect.top;
        const offset = distanceFromCenter * 0.25; // Factor parallax: 0.25 = sutil

        // Mover solo el fondo, no el contenido
        el.style.backgroundPositionY = `calc(50% + ${offset}px)`;
    });

    activeGhosts.forEach(el => {
        const rect = el.parentElement.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        
        // El movimiento se calcula basado en la posición del padre relativo al viewport
        const relativeScroll = (rect.top - window.innerHeight * 0.5);
        const offset = relativeScroll * -0.08; // Factor negativo = se mueve en dirección contraria al scroll visualmente
        
        // Conservamos el centrado -50% X e Y original del CSS, añadiendo el offset
        el.style.transform = `translate(-50%, calc(-50% + ${offset}px))`;
    });

    ticking = false;
}

/**
 * Listener de scroll con throttle via requestAnimationFrame.
 */
function onScroll() {
    if (!ticking) {
        requestAnimationFrame(applyParallax);
        ticking = true;
    }
}

/**
 * Punto de entrada.
 * Llama a esto después de cada renderPage para re-registrar los heroes.
 */
export function initParallax() {
    // No activar en editor ni en móvil
    if (store.get('editorMode')) return;
    if (window.matchMedia('(max-width: 768px)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // El encuadre «en reposo» se aplica YA, sin esperar al primer scroll. Antes la foto arrancaba
    // en `center` y el primer scroll —aunque fuera de vuelta arriba— la movía de golpe a
    // `50% + (alto/2 − top)·0,25`: ~104 px con 830 de alto, justo lo que tapa la cabecera fija
    // (producción, museo, 25-09-2026: «cuando hago scroll se arregla solo»). Ahora ese es el
    // encuadre desde el principio. Dos pasadas: ahora (los fondos ya resueltos, así la foto no
    // salta al aparecer) y tras el retraso de siempre (las fotos de Unsplash que llegan tarde).
    collectHeroes();
    applyParallax();
    setTimeout(() => { collectHeroes(); applyParallax(); }, 600);

    // Evitar listeners duplicados
    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });
}

registerPostRender(initParallax, { previewOnly: true });
